import { Button, Modal } from '@citizen-walk/shared-ui';
import type { AiVerificationAssignmentPlan } from '@citizen-walk/shared-types';
import { useEffect, useMemo, useState } from 'react';
import {
  useRunAssignmentPlan,
  type RunAssignmentPlanResult,
  type RunTargetResult,
} from './useRunAssignmentPlan';

/**
 * 「▶ 配分する」の実行ダイアログ (2026-09-04)。
 *
 * 開いた瞬間に callable を dryRun で呼び、対象ごとの件数 (候補の投稿 / 除外の内訳 / 参加者 / 割当予定 /
 * 割当なしの人数 / 作り直しかどうか) を見せてから本実行する。予約は既定で「実行と同時に公開」なので、
 * この事前確認が唯一の砦。実行後は結果 (生成 / 見送り / 失敗) を同じダイアログで見せる。
 */

export interface RunPlanDialogProps {
  open: boolean;
  districtId: string;
  plan: AiVerificationAssignmentPlan | null;
  /** 一部のセッションだけ実行するとき。undefined = 予約の対象すべて */
  sessionIds?: string[];
  onClose: () => void;
  /** 本実行が終わったとき (成功・一部失敗を問わず) */
  onExecuted?: (result: RunAssignmentPlanResult) => void;
}

function targetLabel(t: RunTargetResult): string {
  return t.sessionId ? (t.sessionName ?? 'セッション') : '地区全体';
}

function excludedSummary(t: RunTargetResult): string {
  const parts: string[] = [];
  const ex = t.posts.excluded;
  if (ex.notAnalyzed > 0) parts.push(`AI 解析待ち ${ex.notAnalyzed}`);
  if (ex.hidden > 0) parts.push(`非公開 ${ex.hidden}`);
  if (ex.unlinked > 0) parts.push(`セッション未設定 ${ex.unlinked}`);
  return parts.length > 0 ? `対象外: ${parts.join(' / ')}` : '';
}

export function RunPlanDialog({
  open,
  districtId,
  plan,
  sessionIds,
  onClose,
  onExecuted,
}: RunPlanDialogProps) {
  const preview = useRunAssignmentPlan();
  const run = useRunAssignmentPlan();
  const [phase, setPhase] = useState<'preview' | 'done'>('preview');
  const sessionKey = (sessionIds ?? []).join('|');

  // 開くたびに dryRun で対象を確認する。phase / run の初期化は親が `key` で remount して行う
  // (effect 内で setState しないため)。
  useEffect(() => {
    if (!open || !plan) return;
    void preview.call({
      districtId,
      planId: plan.id,
      ...(sessionIds && sessionIds.length > 0 ? { sessionIds } : {}),
      dryRun: true,
    });
    // preview は hook の戻り値 (毎 render 新しい object) なので依存に入れない。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, districtId, plan?.id, sessionKey]);

  const okTargets = useMemo(
    () =>
      preview.state.status === 'success'
        ? preview.state.result.targets.filter((t) => t.status === 'ok')
        : [],
    [preview.state],
  );
  const targets = preview.state.status === 'success' ? preview.state.result.targets : [];
  const totalAssignments = okTargets.reduce((a, t) => a + t.assignmentCount, 0);
  const anyReplace = okTargets.some((t) => t.replacedExisting);
  const noAssignmentUsers = okTargets.reduce((a, t) => a + t.usersWithoutAssignment, 0);

  async function handleRun() {
    if (!plan) return;
    const res = await run.call({
      districtId,
      planId: plan.id,
      ...(sessionIds && sessionIds.length > 0 ? { sessionIds } : {}),
    });
    setPhase('done');
    if (res) onExecuted?.(res);
  }

  const title =
    phase === 'done'
      ? '配分の結果'
      : plan?.publishOnRun
        ? '配分して参加者に公開しますか？'
        : '配分しますか？（下書きとして作成）';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      intent="warning"
      closeOnOverlayClick={false}
    >
      {!plan ? null : phase === 'preview' ? (
        <div className="grid grid-cols-1 gap-3 text-sm">
          <p className="text-slate-700">
            <span className="font-medium">{plan.displayName}</span>
            <span className="block text-xs text-slate-500 mt-0.5">
              この予約の配り方で、いまの投稿と参加者から配分を作ります。
            </span>
          </p>

          {preview.state.status === 'pending' && (
            <p className="text-slate-600">対象を確認しています…</p>
          )}
          {preview.state.status === 'error' && (
            <p className="text-danger-700">確認に失敗しました: {preview.state.error.message}</p>
          )}

          {preview.state.status === 'success' && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse" data-testid="run-plan-preview">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-1 pr-2 font-medium">対象</th>
                      <th className="py-1 pr-2 font-medium">対象の投稿</th>
                      <th className="py-1 pr-2 font-medium">参加者</th>
                      <th className="py-1 pr-2 font-medium">割当予定</th>
                      <th className="py-1 font-medium">備考</th>
                    </tr>
                  </thead>
                  <tbody>
                    {targets.map((t) => (
                      <tr key={t.key} className="border-b border-slate-100 align-top">
                        <td className="py-1.5 pr-2 font-medium text-slate-900">{targetLabel(t)}</td>
                        <td className="py-1.5 pr-2 text-slate-700">
                          {t.posts.eligible} 件
                          {excludedSummary(t) && (
                            <span className="block text-[11px] text-slate-500">
                              {excludedSummary(t)}
                            </span>
                          )}
                        </td>
                        <td className="py-1.5 pr-2 text-slate-700">{t.users.eligible} 名</td>
                        <td className="py-1.5 pr-2 text-slate-900 font-medium">
                          {t.status === 'ok' ? `${t.assignmentCount} 件` : '—'}
                        </td>
                        <td className="py-1.5 text-[11px]">
                          {t.status !== 'ok' && (
                            <span className="text-warning-800">見送り: {t.reason}</span>
                          )}
                          {t.status === 'ok' && t.replacedExisting && (
                            <span className="block text-slate-600">既にある配分を作り直します</span>
                          )}
                          {t.status === 'ok' && t.usersWithoutAssignment > 0 && (
                            <span className="block text-warning-800">
                              {t.usersWithoutAssignment} 名には割り当てが発生しません
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="text-xs text-slate-700 grid grid-cols-1 gap-1">
                <li>
                  合計 <span className="font-medium">{totalAssignments} 件</span> を割り当てます。
                </li>
                <li>
                  配分後:{' '}
                  {plan.publishOnRun
                    ? '参加者にすぐ公開されます。'
                    : '下書きとして作られます（一覧で公開してください）。'}
                </li>
                {anyReplace && (
                  <li className="text-slate-600">
                    作り直しでは割り当てが変わることがあります。参加者が入力済みの検証の回答は残ります。
                  </li>
                )}
                {noAssignmentUsers > 0 && (
                  <li className="text-warning-800">
                    割り当てが発生しない参加者がいます。参加セッションに投稿が無いか、AI
                    解析待ちの可能性があります。
                  </li>
                )}
              </ul>
            </>
          )}

          <div className="flex items-center justify-end gap-2 mt-2">
            <Button variant="secondary" onClick={onClose} disabled={run.state.status === 'pending'}>
              キャンセル
            </Button>
            <Button
              onClick={() => void handleRun()}
              disabled={
                preview.state.status !== 'success' ||
                okTargets.length === 0 ||
                run.state.status === 'pending'
              }
            >
              {run.state.status === 'pending' ? '配分中…' : '配分する'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 text-sm">
          {run.state.status === 'error' && (
            <p className="text-danger-700">配分に失敗しました: {run.state.error.message}</p>
          )}
          {run.state.status === 'success' && (
            <ul className="grid grid-cols-1 gap-1 text-xs" data-testid="run-plan-result">
              {run.state.result.targets.map((t) => (
                <li key={t.key} className="flex items-start gap-2">
                  <span
                    className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] ${
                      t.status === 'ok'
                        ? 'bg-success-600 text-white'
                        : t.status === 'skipped'
                          ? 'bg-slate-300 text-slate-800'
                          : 'bg-danger-600 text-white'
                    }`}
                  >
                    {t.status === 'ok' ? '配分済' : t.status === 'skipped' ? '見送り' : '失敗'}
                  </span>
                  <span className="min-w-0">
                    <span className="font-medium text-slate-900">{targetLabel(t)}</span>
                    {t.status === 'ok' && (
                      <span className="text-slate-700">
                        {' '}
                        — {t.assignmentCount} 件 / 参加者 {t.users.eligible} 名
                        {t.published ? '（公開中）' : '（下書き）'}
                      </span>
                    )}
                    {t.status !== 'ok' && <span className="text-slate-600"> — {t.reason}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-end gap-2 mt-2">
            <Button onClick={onClose}>閉じる</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
